import { useQuery } from '@tanstack/react-query';
import { FIB_SCAN_BARS, type Candle, type Timeframe } from '@pipyar/core';
import { api } from '@/lib/api';

export interface FibCandlesResponse {
  symbol: string;
  timeframe: Timeframe;
  storedGmtOffsetSec: number;
  digits: number;
  count: number;
  candles: Candle[];
  live: Candle | null;
}

/** The candles the Fibonacci chart draws, for one timeframe. */
export function useFibCandles(symbol: string | null, timeframe: Timeframe) {
  const limit = FIB_SCAN_BARS[timeframe] ?? 1000;
  return useQuery({
    queryKey: ['fib-candles', symbol, timeframe, limit],
    queryFn: () =>
      api.get<FibCandlesResponse>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}` +
          `&limit=${limit}&includeLive=1`,
      ),
    enabled: Boolean(symbol),
    // A minute, not thirty seconds: the windows are deep (megabytes on M1)
    // and the last bar is moved live by useLiveBar between refetches anyway.
    staleTime: 45_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Teaching draws: Fibonaccis the owner places on the chart by hand.
// ---------------------------------------------------------------------------

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TeachingFib, TeachingFibInput, TeachingFibPatch } from '@pipyar/core';

export function useTeachingFibs(symbol: string | null) {
  return useQuery({
    queryKey: ['fib-teaching', symbol],
    queryFn: () =>
      api.get<{ fibs: TeachingFib[] }>(`/api/fib/teaching?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    staleTime: 30_000,
  });
}

export function useTeachingFibMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fib-teaching'] });

  const create = useMutation({
    mutationFn: (input: TeachingFibInput) =>
      api.post<{ fib: TeachingFib }>('/api/fib/teaching', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TeachingFibPatch }) =>
      api.patch<{ fib: TeachingFib }>(`/api/fib/teaching/${id}`, patch),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del<{ ok: true }>(`/api/fib/teaching/${id}`),
    onSuccess: invalidate,
  });
  return { create, update, remove };
}

// ---------------------------------------------------------------------------
// Auto swings: what the rules draw right now, computed by the API.
// ---------------------------------------------------------------------------

import type { AutoSwing } from '@pipyar/core';

export interface AutoSwingsResponse {
  symbol: string;
  digits: number;
  storedGmtOffsetSec: number;
  /** Per timeframe: one swing per scale that found a distinct one, smallest scale first. */
  swings: Record<string, AutoSwing[]>;
}

export function useAutoSwings(symbol: string | null) {
  return useQuery({
    queryKey: ['fib-auto', symbol],
    queryFn: () => api.get<AutoSwingsResponse>(`/api/fib/auto?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Verdicts: the owner approving or denying a detector swing.
// ---------------------------------------------------------------------------

import type { SwingVerdict, SwingVerdictInput, SwingVerdictPatch } from '@pipyar/core';

export function useVerdictMutations() {
  const queryClient = useQueryClient();
  // The auto response carries the verdicts, so it is what has to refresh.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fib-auto'] });

  const set = useMutation({
    mutationFn: (input: SwingVerdictInput) => api.post<{ verdict: SwingVerdict }>('/api/fib/verdicts', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: SwingVerdictPatch }) =>
      api.patch<{ verdict: SwingVerdict }>(`/api/fib/verdicts/${id}`, patch),
    onSuccess: invalidate,
  });
  const clear = useMutation({
    mutationFn: (id: number) => api.del<{ ok: true }>(`/api/fib/verdicts/${id}`),
    onSuccess: invalidate,
  });
  return { set, update, clear };
}

// ---------------------------------------------------------------------------
// Research notes.
// ---------------------------------------------------------------------------

import type { ResearchNote, Timeframe as Tf } from '@pipyar/core';

export function useResearchNotes() {
  return useQuery({
    queryKey: ['fib-research'],
    queryFn: () => api.get<{ notes: ResearchNote[] }>('/api/fib/research'),
    staleTime: 60_000,
  });
}

export function useResearchNoteMutations() {
  const queryClient = useQueryClient();
  const comment = useMutation({
    mutationFn: ({ id, ownerNote }: { id: number; ownerNote: string }) =>
      api.patch<{ ok: true }>(`/api/fib/research/${id}`, { ownerNote }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fib-research'] }),
  });
  return { comment };
}

/** Candles for a fixed window, for a research chart. */
export function useCandleWindow(symbol: string | null, timeframe: Tf | null, from: number | null, to: number | null) {
  return useQuery({
    queryKey: ['fib-window', symbol, timeframe, from, to],
    queryFn: () =>
      api.get<FibCandlesResponse>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}&from=${from}&to=${to}`,
      ),
    enabled: Boolean(symbol && timeframe && from !== null && to !== null),
    staleTime: 5 * 60_000,
  });
}
